import React, {
  createContext,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ToolbarItem } from '@amzn/aac-chrome-internal-api';
import { CC_MODAL_TOOLBAR_ID, CM_TOOLBAR_ID } from 'src/commons/constants';

export interface ConsolidatedNavContextProviderProps {
  initToolbarItems: ToolbarItem[];
  updateSelectedNavItem?: (toolbarItemId: string, linkId: string) => void;
  landingAppName?: string;
  updateLandingAppName?: (landingAppName: string) => void;
}

export interface ConsolidatedNavContextType {
  isQuickNavModalEnabled: boolean;
  activeCategoryId: string;
  setActiveCategoryId: React.Dispatch<SetStateAction<string>>;
  isNavOpen: boolean;
  setIsNavOpen: React.Dispatch<SetStateAction<boolean>>;
  selectedNavItemId: string;
  setSelectedNavItemId: React.Dispatch<SetStateAction<string>>;
  openModalAppName: string;
  setOpenModalAppName: React.Dispatch<SetStateAction<string>>;
  toolbarItems: Array<ToolbarItem>;
  landingAppName?: string;
  updateLandingAppName?: (landingAppName: string) => void;
  updateSelectedNavItem?: (selectedNavItemId: string, linkId: string) => void;
}

export const ConsolidatedNavContext = createContext<ConsolidatedNavContextType>(
  {
    isQuickNavModalEnabled: false,
    activeCategoryId: '',
    setActiveCategoryId: () => '',
    isNavOpen: false,
    setIsNavOpen: () => false,
    selectedNavItemId: '',
    setSelectedNavItemId: () => '',
    openModalAppName: '',
    setOpenModalAppName: () => '',
    toolbarItems: [],
    updateSelectedNavItem: () => '',
  },
);

const getSelectedNavItemId = (toolbarItems: ToolbarItem[]) =>
  toolbarItems.find((toolbarItem) =>
    toolbarItem.sidebarGroups?.some((group) =>
      group.links.some((link) => link.isSelected),
    ),
  )?.id ?? '';

export const ConsolidatedNavContextProvider: React.FC<
  ConsolidatedNavContextProviderProps
> = ({
  initToolbarItems,
  updateSelectedNavItem,
  landingAppName,
  updateLandingAppName,
  children,
}) => {
  const toolbarItems = useMemo<Array<ToolbarItem>>(
    () => initToolbarItems,
    [initToolbarItems],
  );
  const [selectedNavItemId, setSelectedNavItemId] = useState<string>(() =>
    getSelectedNavItemId(initToolbarItems),
  );
  const [isQuickNavModalEnabled, setIsQuickNavModalEnabled] =
    useState<boolean>(false);
  const [openModalAppName, setOpenModalAppName] = useState<string>('');
  const [activeCategoryId, setActiveCategoryId] = useState<string>(() => {
    if (!selectedNavItemId) {
      return '';
    }

    const lookupNavItemId =
      selectedNavItemId === CC_MODAL_TOOLBAR_ID
        ? CM_TOOLBAR_ID
        : selectedNavItemId;

    const toolbarItem = toolbarItems.find(
      (toolbarItem) => toolbarItem.id === lookupNavItemId,
    );
    return toolbarItem &&
      (toolbarItem.sidebarGroups?.length > 1 ||
        toolbarItem.sidebarGroups[0].links.length > 1)
      ? toolbarItem.id
      : '';
  });
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);

  useEffect(() => {
    setSelectedNavItemId(getSelectedNavItemId(initToolbarItems));
    const containsQuickNav = toolbarItems.find(
      (toolbarItem) =>
        toolbarItem.sidebarGroups?.[0]?.links?.[0].appName ===
        'CampaignCreationModalFrontEnd',
    );
    setIsQuickNavModalEnabled(!!containsQuickNav);
  }, [toolbarItems]);

  return (
    <ConsolidatedNavContext.Provider
      value={{
        isQuickNavModalEnabled,
        activeCategoryId,
        setActiveCategoryId,
        isNavOpen,
        setIsNavOpen,
        selectedNavItemId,
        setSelectedNavItemId,
        openModalAppName,
        setOpenModalAppName,
        toolbarItems,
        landingAppName,
        updateLandingAppName,
        updateSelectedNavItem,
      }}
    >
      {children}
    </ConsolidatedNavContext.Provider>
  );
};

export const useConsolidatedNavContext = (): ConsolidatedNavContextType => {
  const context: ConsolidatedNavContextType = useContext(
    ConsolidatedNavContext,
  );
  if (context === undefined) {
    throw new Error(
      'useConsolidatedNavContext must be used within ConsolidatedNavContextProvider',
    );
  }
  return context;
};
